import { View, Text, Animated } from 'react-native';
import KwaiPlayer from '@kds/player';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { scaleW } from '@/common/style';
import { useDetailPageStore } from '@/page/detail/store';
import { KImage } from '@/component/RtlLayout';
import { formatTime } from './util';
import Slider from 'react-native-slider';
import { useMount, useSetState, useUnmount } from 'ahooks';
import { getNetworkType } from '@/common/device-info';
import { usePageContext } from '@/common/context/pageContext';
import { userId } from '@/common/const';
import { KTouchableWithoutFeedback } from '@/component/RtlLayout/Touchable';

type SwiperVideoProps = {
    uri: string; // 视频链接
    poster?: string; //封面
    isPreview?: boolean; // 是否预览
    onPress?: () => void; // 点击事件
};

export type PureVideoPreviewHandle = {
    handleVideoPause: () => void;
};

function SwiperVideo(props: SwiperVideoProps) {
    const { uri, isPreview = false, onPress = () => undefined, poster } = props;
    const { videoStatus, setVideoStatus } = useDetailPageStore(s => ({
        videoStatus: s.videoStatus,
        setVideoStatus: s.setVideoStatus,
    }));
    const { logger } = usePageContext();

    useEffect(() => {
        logger.sendShow({
            action: 'ITEM_VIDEO',
            params: {
                media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                uid: userId,
            },
        });
        getNetworkType().then(res => {
            if (res === 'WIFI') {
                setVideoStatus({
                    paused: false,
                });
            }
        });
    }, []);

    const videoRef = useRef(null);

    // 进度条
    const progress = videoStatus.seekableDuration ? (
        <View
            style={{"position":"absolute","height":"0.9661835748792271rem","width":"100%","backgroundColor":"rgba(0,0,0,0.3)","bottom":"0rem","left":"0rem"}}>
            <View
                style={{
                    width: Math.ceil((scaleW(414) * videoStatus.currentTime) / videoStatus.seekableDuration),
                    height: '100%',
                    borderTopRightRadius: 2,
                    // borderBottomRightRadius: 5,
                    backgroundColor: '#FF4906',
                }}
            />
        </View>
    ) : null;

    const onClick = () => {
        setVideoStatus({
            seek: videoStatus.currentTime,
        });
        onPress();
    };

    useEffect(() => {
        videoRef?.current?.seek(videoStatus.seek);
    }, [videoStatus.seek]);

    const opacity = useRef(new Animated.Value(1)).current; // 播放按钮的透明度
    const timer = useRef(null);

    const toTransparent = () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            Animated.sequence([
                Animated.timing(opacity, {
                    toValue: 0, // 透明度变为0（完全透明）
                    duration: 1000, // 动画持续时间1000毫秒
                    useNativeDriver: true, // 使用原生动画驱动（如果可能）
                }),
            ]).start();
        });
    };

    useEffect(() => {
        if (videoStatus.paused) {
            opacity.setValue(1);
        } else {
            toTransparent();
        }
    }, [opacity, videoStatus.paused]);

    useUnmount(() => {
        clearTimeout(timer.current);
    });

    useEffect(() => {
        const logClickVideo = (pause: boolean) => {
            logger.sendClick({
                action: 'ITEM_VIDEO',
                params: {
                    media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                    total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                    item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                    uid: userId,
                    click_state: pause ? 'pause' : 'continue',
                },
            });
        };
        logClickVideo(videoStatus.paused);
    }, [videoStatus.paused]);

    const lastTime = useRef(0);

    const logVideoProgress = (preTimestamp, currentTimestamp) => {
        const stateMap = {
            not_start: 0,
            0: '0_10',
            1: '10_20',
            2: '20_30',
            3: '30_40',
            4: '40_50',
            5: '50_60',
            6: '60_70',
            7: '70_80',
            8: '80_90',
            9: '90_100',
            end: 'done',
        };
        if (currentTimestamp === 0) {
            logger.sendShow({
                action: 'PLAY_ITEM_VIDEO_STATE',
                params: {
                    media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                    total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                    item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                    uid: userId,
                    play_progress: stateMap.not_start,
                },
            });
        } else if (currentTimestamp === useDetailPageStore.getState()?.videoStatus.seekableDuration) {
            logger.sendShow({
                action: 'PLAY_ITEM_VIDEO_STATE',
                params: {
                    media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                    total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                    item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                    uid: userId,
                    play_progress: stateMap.end,
                },
            });
        } else {
            // 如果总长度为1000s，当播放了100s时，期望得到结果为1
            const curentProgress = Math.floor((currentTimestamp / useDetailPageStore.getState()?.videoStatus.seekableDuration) * 10);
            const preProgress = Math.floor((preTimestamp / useDetailPageStore.getState()?.videoStatus.seekableDuration) * 10);
            if (curentProgress !== preProgress) {
                logger.sendShow({
                    action: 'PLAY_ITEM_VIDEO_STATE',
                    params: {
                        media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                        total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                        item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                        uid: userId,
                        play_progress: stateMap[curentProgress],
                    },
                });
            }
        }
    };

    return (
        <KTouchableWithoutFeedback onPress={onClick}>
            <View style={{"backgroundColor":"black","width":"100.00000000000001rem","height":"100.00000000000001rem"}}>
                <KwaiPlayer
                    source={{
                        uri, // 资源链接，支持磁盘，内置，远程以及多码率等多种类型
                    }}
                    muted={isPreview || videoStatus.muted}
                    paused={isPreview || videoStatus.paused}
                    style={{"width":"100%","height":"100%"}}
                    poster={!videoStatus.seek && poster}
                    onProgress={params => {
                        setVideoStatus({
                            currentTime: params.currentTime,
                            playableDuration: params.playableDuration,
                            seekableDuration: params.seekableDuration,
                        });
                        logVideoProgress(lastTime.current, params.currentTime);
                        lastTime.current = params.currentTime;
                    }}
                    onLoad={params => {
                        setVideoStatus({
                            playableDuration: params.duration,
                            seekableDuration: params.duration,
                        });
                    }}
                    onEnd={() => {
                        videoRef?.current?.seek(0);
                        setVideoStatus({
                            currentTime: 0,
                            paused: true,
                        });
                        logVideoProgress(lastTime.current, useDetailPageStore.getState().videoStatus.seekableDuration);
                        lastTime.current = useDetailPageStore.getState().videoStatus.seekableDuration;
                    }}
                    ref={videoRef}
                />
                <View
                    style={{"position":"absolute","top":"0rem","width":"100%","height":"100%","alignItems":"center","justifyContent":"center"}}>
                    <Animated.View style={{ opacity: opacity }}>
                        <KTouchableWithoutFeedback
                            onPress={() => {
                                setVideoStatus({
                                    paused: !videoStatus.paused,
                                });
                            }}>
                            <KImage
                                source={{
                                    uri: videoStatus.paused
                                        ? 'https://cdn-static.kwai.net/kos/s101/nlav11312/BYsvVJNA_2024-07-19-11-56-52.png'
                                        : 'https://cdn-static.kwai.net/kos/s101/nlav11312/ksxNRwmS_2024-07-19-12-04-51.png',
                                }}
                                style={{"width":"11.594202898550726rem","height":"11.835748792270532rem"}}
                            />
                        </KTouchableWithoutFeedback>
                    </Animated.View>
                </View>

                {progress}
            </View>
        </KTouchableWithoutFeedback>
    );
}

export function PreviewVideo(props: SwiperVideoProps) {
    const { uri, poster } = props;
    const { videoStatus, setVideoStatus } = useDetailPageStore(s => ({
        videoStatus: s.videoStatus,
        setVideoStatus: s.setVideoStatus,
    }));
    const videoRef = useRef(null);

    const opacity = useRef(new Animated.Value(1)).current; // 播放按钮的透明度
    const timer = useRef(null);
    const { logger } = usePageContext();

    const toTransparent = () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            Animated.sequence([
                Animated.timing(opacity, {
                    toValue: 0, // 透明度变为0（完全透明）
                    duration: 1000, // 动画持续时间1000毫秒
                    useNativeDriver: true, // 使用原生动画驱动（如果可能）
                }),
            ]).start();
        });
    };

    useEffect(() => {
        if (videoStatus.paused) {
            opacity.setValue(1);
        } else {
            toTransparent();
        }
    }, [opacity, videoStatus.paused]);

    useMount(() => {
        logger.sendShow({
            action: 'ITEM_VIDEO',
            params: {
                media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                uid: userId,
            },
        });
    });

    useEffect(() => {
        const logClickVideo = (pause: boolean) => {
            logger.sendClick({
                action: 'ITEM_VIDEO',
                params: {
                    media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                    total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                    item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                    uid: userId,
                    click_state: pause ? 'pause' : 'continue',
                },
            });
        };
        logClickVideo(videoStatus.paused);
    }, [videoStatus.paused]);

    useUnmount(() => {
        clearTimeout(timer.current);
    });
    const [sliderValue, setSliderValue] = useState(undefined);
    const sliderState = useRef('none');

    const lastTime = useRef(0);

    const logVideoProgress = (preTimestamp, currentTimestamp) => {
        const stateMap = {
            not_start: 0,
            0: '0_10',
            1: '10_20',
            2: '20_30',
            3: '30_40',
            4: '40_50',
            5: '50_60',
            6: '60_70',
            7: '70_80',
            8: '80_90',
            9: '90_100',
            end: 'done',
        };
        if (currentTimestamp === 0) {
            logger.sendShow({
                action: 'PLAY_ITEM_VIDEO_STATE',
                params: {
                    media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                    total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                    item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                    uid: userId,
                    play_progress: stateMap.not_start,
                },
            });
        } else if (currentTimestamp === useDetailPageStore.getState()?.videoStatus.seekableDuration) {
            logger.sendShow({
                action: 'PLAY_ITEM_VIDEO_STATE',
                params: {
                    media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                    total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                    item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                    uid: userId,
                    play_progress: stateMap.end,
                },
            });
        } else {
            // 如果总长度为1000s，当播放了100s时，期望得到结果为1
            const curentProgress = Math.floor((currentTimestamp / useDetailPageStore.getState()?.videoStatus.seekableDuration) * 10);
            const preProgress = Math.floor((preTimestamp / useDetailPageStore.getState()?.videoStatus.seekableDuration) * 10);
            if (curentProgress !== preProgress) {
                logger.sendShow({
                    action: 'PLAY_ITEM_VIDEO_STATE',
                    params: {
                        media_id: useDetailPageStore.getState()?.pageRawData?.coverPhoto?.photoId,
                        total_duration: useDetailPageStore.getState()?.videoStatus.seekableDuration,
                        item_id: useDetailPageStore.getState()?.pageRawData?.itemId,
                        uid: userId,
                        play_progress: stateMap[curentProgress],
                    },
                });
            }
        }
    };

    // 进度条
    const progress = (
        <View
            style={{"position":"absolute","height":"4.830917874396135rem","width":"100%","backgroundColor":"black","bottom":"0rem","left":"0rem","flexDirection":"row","alignItems":"center","justifyContent":"space-between"}}>
            <Text
                style={{"marginHorizontal":"4.5893719806763285rem","color":"white"}}>
                {formatTime(sliderValue ?? videoStatus.currentTime)}
            </Text>
            <View style={{"flex":1}}>
                <Slider
                    value={sliderValue ?? videoStatus.currentTime} // 防止进度条闪动，在拖动进度条时，使用sliderValue
                    minimumValue={0}
                    maximumValue={videoStatus.seekableDuration}
                    minimumTrackTintColor="#FFFFFF"
                    maximumTrackTintColor="rgba(255, 255, 255, 0.40)"
                    onSlidingComplete={val => {
                        setVideoStatus({
                            currentTime: val,
                        });
                        videoRef.current?.seek(val);
                        setSliderValue(undefined);
                        sliderState.current = 'none';
                    }}
                    onValueChange={val => {
                        setSliderValue(val);
                    }}
                    onSlidingStart={() => {
                        sliderState.current = 'moving';
                    }}
                    thumbTintColor="white"
                    thumbStyle={{
                        width: scaleW(20),
                        height: scaleW(20),
                        borderRadius: scaleW(10),
                    }}
                />
            </View>
            <Text
                style={{"marginHorizontal":"4.5893719806763285rem","color":"white"}}>
                {formatTime(videoStatus.seekableDuration)}
            </Text>
            <KTouchableWithoutFeedback
                onPress={() => {
                    setVideoStatus({
                        muted: !videoStatus.muted,
                    });
                }}>
                <KImage
                    source={{
                        uri: videoStatus.muted
                            ? 'https://cdn-static.kwai.net/kos/s101/nlav11312/DzyfGeHQ_2024-07-19-14-55-16.png'
                            : 'https://cdn-static.kwai.net/kos/s101/nlav11312/BJKVsyRl_2024-07-19-14-54-56.png',
                    }}
                    style={{"width":"6.76328502415459rem","height":"6.76328502415459rem","marginRight":"2.8985507246376816rem"}}
                />
            </KTouchableWithoutFeedback>
        </View>
    );

    useUnmount(() => {
        setVideoStatus({
            seek: videoStatus.currentTime,
        });
    });

    return (
        // 使用一个TouchableWithoutFeedback来隔离点击事件，表现：点击视频区域不会响应swiper的默认点击行为
        <KTouchableWithoutFeedback>
            <View
                style={{"height":"90%","justifyContent":"space-between","paddingVertical":46}}>
                <KTouchableWithoutFeedback
                    onPress={() => {
                        setVideoStatus({
                            paused: !videoStatus.paused,
                        });
                    }}>
                    <View style={{"backgroundColor":"black","width":"100.00000000000001rem","flex":1,"marginBottom":"4.830917874396135rem"}}>
                        <KwaiPlayer
                            source={{
                                uri, // 资源链接，支持磁盘，内置，远程以及多码率等多种类型
                            }}
                            muted={videoStatus.muted}
                            paused={videoStatus.paused || sliderState.current === 'moving'}
                            poster={!videoStatus.seek && poster}
                            onLoad={() => {
                                videoRef?.current?.seek(videoStatus.seek);
                            }}
                            onEnd={() => {
                                videoRef?.current?.seek(0);
                                setVideoStatus({
                                    currentTime: 0,
                                    paused: true,
                                });
                                logVideoProgress(lastTime.current, useDetailPageStore.getState().videoStatus.seekableDuration);
                                lastTime.current = useDetailPageStore.getState().videoStatus.seekableDuration;
                            }}
                            style={{"width":"100%","height":"100%"}}
                            onProgress={params => {
                                setVideoStatus({
                                    currentTime: params.currentTime,
                                    playableDuration: params.playableDuration,
                                    seekableDuration: params.seekableDuration,
                                    target: params.target,
                                });
                                logVideoProgress(lastTime.current, params.currentTime);
                                lastTime.current = params.currentTime;
                            }}
                            ref={videoRef}
                        />
                        <View
                            style={{"position":"absolute","top":"0rem","width":"100%","height":"100%","alignItems":"center","justifyContent":"center"}}>
                            <Animated.View style={{ opacity: opacity }}>
                                <KImage
                                    source={{
                                        uri: videoStatus.paused
                                            ? 'https://cdn-static.kwai.net/kos/s101/nlav11312/BYsvVJNA_2024-07-19-11-56-52.png'
                                            : 'https://cdn-static.kwai.net/kos/s101/nlav11312/ksxNRwmS_2024-07-19-12-04-51.png',
                                    }}
                                    style={{"width":"11.594202898550726rem","height":"11.835748792270532rem"}}
                                />
                            </Animated.View>
                        </View>
                    </View>
                </KTouchableWithoutFeedback>
                {progress}
            </View>
        </KTouchableWithoutFeedback>
    );
}

// 不包含store和埋点
export const PurePreviewVideo = forwardRef<PureVideoPreviewHandle, SwiperVideoProps>((props, ref) => {
    const { uri, poster } = props;
    const [videoStatus, setVideoStatus] = useSetState({
        currentTime: 0,
        seekableDuration: 0,
        seek: 0,
        target: 0,
        playableDuration: 0,
        naturalSize: {
            width: 0,
            height: 0,
            orientation: null as 'landscape' | 'portrait',
        },
        muted: true,
        paused: true,
    });

    const videoRef = useRef(null);

    useImperativeHandle(ref, () => {
        return {
            handleVideoPause: () => {
                setVideoStatus({
                    paused: true,
                });
            },
        };
    });

    const opacity = useRef(new Animated.Value(1)).current; // 播放按钮的透明度
    const timer = useRef(null);

    const toTransparent = () => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            Animated.sequence([
                Animated.timing(opacity, {
                    toValue: 0, // 透明度变为0（完全透明）
                    duration: 1000, // 动画持续时间1000毫秒
                    useNativeDriver: true, // 使用原生动画驱动（如果可能）
                }),
            ]).start();
        });
    };

    useEffect(() => {
        if (videoStatus.paused) {
            opacity.setValue(1);
        } else {
            toTransparent();
        }
    }, [opacity, videoStatus.paused]);

    useUnmount(() => {
        clearTimeout(timer.current);
    });

    const [sliderValue, setSliderValue] = useState(undefined);
    const sliderState = useRef('none');

    const lastTime = useRef(0);

    // 进度条
    const progress = (
        <View
            style={{"position":"absolute","height":"4.830917874396135rem","width":"100%","backgroundColor":"black","bottom":"0rem","left":"0rem","flexDirection":"row","alignItems":"center","justifyContent":"space-between"}}>
            <Text
                style={{"marginHorizontal":"4.5893719806763285rem","color":"white"}}>
                {formatTime(sliderValue ?? videoStatus.currentTime)}
            </Text>
            <View style={{"flex":1}}>
                <Slider
                    value={sliderValue ?? videoStatus.currentTime} // 防止进度条闪动，在拖动进度条时，使用sliderValue
                    minimumValue={0}
                    maximumValue={videoStatus.seekableDuration}
                    minimumTrackTintColor="#FFFFFF"
                    maximumTrackTintColor="rgba(255, 255, 255, 0.40)"
                    onSlidingComplete={val => {
                        setVideoStatus({
                            currentTime: val,
                        });
                        videoRef.current?.seek(val);
                        setSliderValue(undefined);
                        sliderState.current = 'none';
                    }}
                    onValueChange={val => {
                        setSliderValue(val);
                    }}
                    onSlidingStart={() => {
                        sliderState.current = 'moving';
                    }}
                    thumbTintColor="white"
                    thumbStyle={{
                        width: scaleW(20),
                        height: scaleW(20),
                        borderRadius: scaleW(10),
                    }}
                />
            </View>
            <Text
                style={{"marginHorizontal":"4.5893719806763285rem","color":"white"}}>
                {formatTime(videoStatus.seekableDuration)}
            </Text>
            <KTouchableWithoutFeedback
                onPress={() => {
                    setVideoStatus({
                        muted: !videoStatus.muted,
                    });
                }}>
                <KImage
                    source={{
                        uri: videoStatus.muted
                            ? 'https://cdn-static.kwai.net/kos/s101/nlav11312/DzyfGeHQ_2024-07-19-14-55-16.png'
                            : 'https://cdn-static.kwai.net/kos/s101/nlav11312/BJKVsyRl_2024-07-19-14-54-56.png',
                    }}
                    style={{"width":"6.76328502415459rem","height":"6.76328502415459rem","marginRight":"2.8985507246376816rem"}}
                />
            </KTouchableWithoutFeedback>
        </View>
    );

    useUnmount(() => {
        setVideoStatus({
            seek: videoStatus.currentTime,
        });
    });

    return (
        // 使用一个TouchableWithoutFeedback来隔离点击事件，表现：点击视频区域不会响应swiper的默认点击行为
        <KTouchableWithoutFeedback>
            <View
                style={{"height":"90%","justifyContent":"space-between","paddingVertical":46}}>
                <KTouchableWithoutFeedback
                    onPress={() => {
                        setVideoStatus({
                            paused: !videoStatus.paused,
                        });
                    }}>
                    <View style={{"backgroundColor":"black","width":"100.00000000000001rem","flex":1,"marginBottom":"4.830917874396135rem"}}>
                        <KwaiPlayer
                            source={{
                                uri, // 资源链接，支持磁盘，内置，远程以及多码率等多种类型
                            }}
                            muted={videoStatus.muted}
                            paused={videoStatus.paused || sliderState.current === 'moving'}
                            poster={!videoStatus.seek && poster}
                            onLoad={() => {
                                videoRef?.current?.seek(videoStatus.seek);
                            }}
                            onEnd={() => {
                                videoRef?.current?.seek(0);
                                setVideoStatus({
                                    currentTime: 0,
                                    paused: true,
                                });
                                lastTime.current = useDetailPageStore.getState().videoStatus.seekableDuration;
                            }}
                            style={{"width":"100%","height":"100%"}}
                            onProgress={params => {
                                setVideoStatus({
                                    currentTime: params.currentTime,
                                    playableDuration: params.playableDuration,
                                    seekableDuration: params.seekableDuration,
                                    target: params.target,
                                });
                                lastTime.current = params.currentTime;
                            }}
                            ref={videoRef}
                        />
                        <View
                            style={{"position":"absolute","top":"0rem","width":"100%","height":"100%","alignItems":"center","justifyContent":"center"}}>
                            <Animated.View style={{ opacity: opacity }}>
                                <KImage
                                    source={{
                                        uri: videoStatus.paused
                                            ? 'https://cdn-static.kwai.net/kos/s101/nlav11312/BYsvVJNA_2024-07-19-11-56-52.png'
                                            : 'https://cdn-static.kwai.net/kos/s101/nlav11312/ksxNRwmS_2024-07-19-12-04-51.png',
                                    }}
                                    style={{"width":"11.594202898550726rem","height":"11.835748792270532rem"}}
                                />
                            </Animated.View>
                        </View>
                    </View>
                </KTouchableWithoutFeedback>
                {progress}
            </View>
        </KTouchableWithoutFeedback>
    );
});

export default SwiperVideo;
